import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, InputBase, Stack, Typography } from '@mui/material';

export type PaneDesignSection = 'data' | 'style' | 'advanced';

type Presentation = { section: PaneDesignSection; query: string; focusKey?: string };
type SettingResult = { key: string; label: string; section: PaneDesignSection };

const PresentationContext = createContext<Presentation | null>(null);
const SectionContext = createContext('root');

export const usePanePresentation = () => useContext(PresentationContext);
export const usePaneSectionId = () => useContext(SectionContext);
export const PaneSectionId = SectionContext.Provider;

const STYLE = new Set(['chart', 'chart-colours', 'appearance', 'figure-style', 'lens-display', 'lens-tabs', 'lens-layout', 'lens-sections']);
const ADVANCED = new Set(['code', 'measures', 'measure-lens-wide', 'every', 'edit-block-about', 'edit-block-data', 'lens-every']);

export function paneSectionOf(id: string): PaneDesignSection {
  if (STYLE.has(id)) return 'style';
  if (ADVANCED.has(id)) return 'advanced';
  return 'data';
}

export function settingMatches(query: string, label: string, keywords: ReadonlyArray<string> = []): boolean {
  const needle = query.trim().toLowerCase();
  return !needle || [label, ...keywords].some((text) => text.toLowerCase().includes(needle));
}

/** Presentation only. The selected section and search never enter the saved Lens spec. */
export function PanePresentation({ active, section, onSection, emptyHint = 'No setting matches here.', children }: { active: boolean; section: PaneDesignSection; onSection: (section: PaneDesignSection) => void; emptyHint?: string; children: React.ReactNode }) {
  const root = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Array<SettingResult>>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [focusKey, setFocusKey] = useState<string>();

  useEffect(() => {
    if (!active || !query.trim()) {
      setResults([]);
      return;
    }
    const matches = [...(root.current?.querySelectorAll<HTMLElement>('[data-setting-row], [data-settings-section]') ?? [])]
      .filter((element) => element.getClientRects().length > 0)
      .map((element) => ({
        key: element.dataset.settingKey ?? '',
        label: element.dataset.settingResult ?? element.dataset.settingRow ?? element.dataset.settingsTitle ?? '',
        keywords: element.dataset.settingKeywords?.split('|') ?? [],
        section: (element.dataset.settingSection ?? 'data') as PaneDesignSection,
      }))
      .filter((entry) => entry.key && settingMatches(query, entry.label, entry.keywords))
      .map(({ key, label, section }) => ({ key, label, section }));
    const unique = [...new Map(matches.map((entry) => [entry.key, entry])).values()];
    setResults((previous) => JSON.stringify(previous) === JSON.stringify(unique) ? previous : unique);
  }, [active, query, children]);

  useEffect(() => {
    if (!focusKey || query) return undefined;
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const target = root.current?.querySelector<HTMLElement>(`[data-setting-key="${CSS.escape(focusKey)}"]`);
        target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        const control = target?.querySelector<HTMLElement>('input, textarea, [role="combobox"]') ?? target?.querySelector<HTMLElement>('button:not([aria-label^="Reset"])');
        (control ?? target)?.focus();
        setFocusKey(undefined);
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusKey, section, query]);

  const jump = (result: SettingResult) => {
    onSection(result.section);
    setFocusKey(result.key);
    setQuery('');
    setResults([]);
    setActiveIndex(0);
  };

  if (!active) return <>{children}</>;
  return (
    <PresentationContext.Provider value={{ section, query: query.trim().toLowerCase(), focusKey }}>
      <Box data-pane-presentation="grouped" ref={root}>
        <InputBase
          aria-activedescendant={query && results.length ? `lens-setting-result-${activeIndex}` : undefined}
          aria-controls="lens-setting-results"
          aria-expanded={Boolean(query)}
          inputProps={{ 'aria-label': 'Find a setting', role: 'combobox' }}
          onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') { event.stopPropagation(); setQuery(''); return; }
            if (event.key === 'ArrowDown' && results.length) { event.preventDefault(); setActiveIndex((index) => (index + 1) % results.length); }
            if (event.key === 'ArrowUp' && results.length) { event.preventDefault(); setActiveIndex((index) => (index - 1 + results.length) % results.length); }
            if (event.key === 'Enter' && results.length) { event.preventDefault(); jump(results[activeIndex]); }
          }}
          placeholder="Find a setting"
          startAdornment={<SearchIcon sx={{ color: 'text.secondary', fontSize: 16, mr: 0.75 }} />}
          sx={{ bgcolor: 'action.hover', borderRadius: '8px', fontSize: 13, mb: 0.5, mx: 1.5, px: 1, py: 0.25, width: 'calc(100% - 24px)' }}
          value={query}
        />
        {query ? (
          <Box aria-label="Matching settings" id="lens-setting-results" role="listbox" sx={{ borderBottom: 1, borderColor: 'divider', maxHeight: 200, mx: 1.5, overflowY: 'auto', pb: 0.5 }}>
            {results.length ? results.map((result, index) => (
              <Button
                aria-selected={index === activeIndex}
                id={`lens-setting-result-${index}`}
                key={result.key}
                onClick={() => jump(result)}
                role="option"
                size="small"
                sx={{ display: 'flex', justifyContent: 'space-between', textTransform: 'none', width: '100%' }}
              >
                {result.label}<Typography color="text.secondary" sx={{ fontSize: 11 }}>{result.section}</Typography>
              </Button>
            )) : <Typography color="text.secondary" role="status" sx={{ fontSize: 12, py: 1 }}>{emptyHint}</Typography>}
          </Box>
        ) : null}
        <Stack aria-label="Settings sections" direction="row" role="group" sx={{ bgcolor: 'action.hover', borderRadius: '8px', gap: 0.25, m: 1.5, mt: 1, p: 0.25 }}>
          {([['data', 'Data'], ['style', 'Style'], ['advanced', 'Advanced']] as const).map(([key, label]) => (
            <Button aria-pressed={section === key} key={key} onClick={() => { setQuery(''); onSection(key); }} size="small" sx={{ bgcolor: section === key ? 'background.paper' : 'transparent', color: section === key ? 'text.primary' : 'text.secondary', flex: 1, fontSize: 12, textTransform: 'none' }}>{label}</Button>
          ))}
        </Stack>
        {children}
      </Box>
    </PresentationContext.Provider>
  );
}
